/* eslint-disable max-statements */
import { Button, Grid, LoadingOverlay, Stack, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { DatePickerInput, TextInput } from '@components';
import { useCreateWallet, useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';

const businessCardSchema = z.object({
  idCard: z.object({
    ownerName: z.string().min(1, { message: 'Owner name required' }),
    expirationDate: z
      .date()
      .nullable()
      .refine((date) => date instanceof Date && !isNaN(date.getTime()), { message: 'Expiration date required' }),
  }),
  businessLicense: z.object({
    nameEN: z.string().min(1, { message: 'Name EN required' }),
    nameCN: z.string().min(1, { message: 'Name CN required' }),
    registrationNumber: z.string().min(1, { message: 'Registration number required' }),
  }),
  eori: z.object({
    number: z.string().min(1, { message: 'EORI number required' }),
    address: z.object({
      street: z.string().min(1, { message: 'EORI street required' }),
      city: z.string().min(1, { message: 'EORI city required' }),
      zipCode: z.string().min(1, { message: 'EORI zip code required' }),
      country: z.string().min(1, { message: 'EORI country required' }),
    }),
  }),
  vat: z.object({
    number: z.string().min(1, { message: 'VAT number required' }),
    address: z.object({
      street: z.string().min(1, { message: 'VAT street required' }),
      city: z.string().min(1, { message: 'VAT city required' }),
      zipCode: z.string().min(1, { message: 'VAT zip code required' }),
      country: z.string().min(1, { message: 'VAT country required' }),
    }),
  }),
});

type BusinessCardFormValues = z.infer<typeof businessCardSchema>;

export const WalletInformation = () => {
  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const afterRegisterParam = queryParams.current.get('after-register');
  const isAfterRegister = useMemo(() => Boolean(afterRegisterParam), [afterRegisterParam]);

  const { data: declarant, isLoading } = useDeclarant();
  const createWalletMutation = useCreateWallet();
  const updateDeclarantMutation = useUpdateDeclarant();
  const { data: settings } = useSettings();

  const buildFormInitialValues = (): BusinessCardFormValues => ({
    idCard: {
      ownerName: declarant?.company?.idCard?.ownerName ?? '',
      expirationDate: declarant?.company?.idCard?.expirationDate ?? null,
    },
    businessLicense: {
      nameEN: declarant?.company?.businessLicense?.nameEN ?? '',
      nameCN: declarant?.company?.businessLicense?.nameCN ?? '',
      registrationNumber: declarant?.company?.businessLicense?.registrationNumber ?? '',
    },
    eori: {
      number: declarant?.company?.eori?.number ?? '',
      address: {
        street: declarant?.company?.eori?.address?.street ?? '',
        city: declarant?.company?.eori?.address?.city ?? '',
        zipCode: declarant?.company?.eori?.address?.zipCode ?? '',
        country: declarant?.company?.eori?.address?.country ?? '',
      },
    },
    vat: {
      number: declarant?.company?.vat?.number ?? '',
      address: {
        street: declarant?.company?.vat?.address?.street ?? '',
        city: declarant?.company?.vat?.address?.city ?? '',
        zipCode: declarant?.company?.vat?.address?.zipCode ?? '',
        country: declarant?.company?.vat?.address?.country ?? '',
      },
    },
  });

  const form = useFormWithNameInputProp<BusinessCardFormValues>({
    initialValues: buildFormInitialValues(),
    validate: zodResolver(businessCardSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildFormInitialValues());
  }, [declarant]);

  const onSubmit = async (values) => {
    await updateDeclarantMutation.mutateAsync({
      declarantId: declarant._id,
      declarant: {
        company: values,
      },
    });

    if (!declarant?.walletEndpoint) {
      await createWalletMutation.mutateAsync();
    }
  };

  useEffect(() => {
    const toastId = 'wallet-toast';

    if (isAfterRegister && !toast.isActive(toastId)) {
      toast.success('Account information saved successfully! In order to link with a broker you have to fill in the remaining information', {
        autoClose: false,
        toastId,
      });
      queryParams.current.delete('after-register');
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [isAfterRegister]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack py="xl" align="flex-start">
      <Title order={2}>Wallet information</Title>

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap={40} mb={32}>
          <Stack gap="sm">
            <Title order={4}>ID Card</Title>
            <Grid>
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('idCard.ownerName')} label="Owner name" withAsterisk />
              </Grid.Col>

              <Grid.Col span={6}>
                <DatePickerInput {...form.getInputProps('idCard.expirationDate')} label="Expiration Date" withAsterisk />
              </Grid.Col>
            </Grid>
          </Stack>

          <Stack gap="sm">
            <Title order={4}>Chinese business license</Title>
            <Grid>
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('businessLicense.nameEN')} label="Company name EN" withAsterisk />
              </Grid.Col>

              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('businessLicense.nameCN')} label="Company name CN" withAsterisk />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('businessLicense.registrationNumber')} label="Registration number" withAsterisk />
              </Grid.Col>
            </Grid>
          </Stack>

          <Stack gap="sm">
            <Title order={4}>EORI</Title>
            <Grid>
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('eori.number')} label="EORI number" withAsterisk />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('eori.address.street')} label="Street & number" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('eori.address.city')} label="City" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('eori.address.zipCode')} label="Zip code" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('eori.address.country')} label="Country" withAsterisk />
              </Grid.Col>
            </Grid>
          </Stack>

          <Stack gap="sm">
            <Title order={4}>VAT </Title>
            <Grid>
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('vat.number')} label="VAT number" withAsterisk />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vat.address.street')} label="Street & number" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vat.address.city')} label="City" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vat.address.zipCode')} label="Zip code" withAsterisk />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vat.address.country')} label="Country" withAsterisk />
              </Grid.Col>
            </Grid>
          </Stack>
        </Stack>

        {declarant?.walletEndpoint ? (
          <Button type="submit" disabled={!form.isValid()} variant="outline" size="lg">
            Update wallet information
          </Button>
        ) : (
          <Button type="submit" disabled={!form.isValid()} variant="outline" size="lg">
            Create wallet
          </Button>
        )}
      </form>
    </Stack>
  );
};
